import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import type { VloerkleedCartItem, PricingBootstrap } from '../lib/pricing/types'
import { calculateArea, calculateVloerkleedItemPrice, calculateVloerkleedTotalPrice } from '../lib/pricing/vloerkleed'
import { centsToEuros } from '../lib/pricing/money'
import { fetchOfferteRoutes, type OfferteRouteOption } from '../lib/offertes'
import { DEFAULT_OFFERTE_PRIMARY_SKU, isCountBasedSku } from '../lib/offerte-products'
import { applyWederverkoperOverrides, WEDERVERKOPER_SKU } from '../lib/pricing/wederverkoper'

// ─── TYPES ──────────────────────────────────────────────────

export interface CustomerData {
  // Optioneel: nieuwe klanten (native /nieuwe-aanvraag) hebben nog geen Account;
  // create.ts spreadt Existing_Account__c alleen indien aanwezig en de
  // AccountResolver/converter maakt dan zelf een (Person)Account aan.
  accountId?: string
  firstName: string
  lastName: string
  email: string
  phone: string
  street: string
  postcode: string
  city: string
  // B2B: contactpersoon binnen het Account. Mapped naar Lead.Existing_Contact__c
  // zodat LeadToOpportunityConverter de bestaande Contact hergebruikt bij conversie.
  contactId?: string
  // B2B: bedrijfsnaam (Account.Name als isPersonAccount=false). Mapped naar Lead.Company —
  // standaard required field op Lead voor non-Person flows.
  companyName?: string
  // Buitenland (BE/DE): vrije tekst naar Lead.Country. Afwezig = Nederland.
  country?: string
  // Buiten het reguliere werkgebied (buitenland of NL buiten dienstgebied):
  // bootstrap/pricing via het vaste 'Buiten werkgebied'-gebied, geen
  // routevoorstellen, WO krijgt handmatig-inplannen vlaggen bij conversie.
  buitenWerkgebied?: boolean
}

interface SubmitLineItem {
  service: string
  sku: string
  qty: number
  unit_price: number
  subtotal: number
  total_price: number
  notes?: string
  parent_sku?: string
  parent_index?: number
  meta?: {
    dims?: {
      length_cm: number
      width_cm: number
      diameter_cm: number
      area_m2: number
    }
  }
}

export interface PendingCapacityBump {
  routeId: string
  type: 'pickup'
  delta: number
  reason: string
}

interface OrderPanelState {
  // Panel
  isOpen: boolean
  isSubmitting: boolean
  submitResult: {
    ok: boolean
    leadId?: string
    leadnummer?: string
    workOrderId?: string
    workOrderNumber?: string
    error?: string
    capacityBumpWarning?: string
  } | null

  // Customer
  customer: CustomerData | null

  // Bootstrap — `bootstrap` is wat de UI/pricing gebruikt (incl. eventuele
  // wederverkoper-overrides); `bootstrapRaw` is de onbewerkte calculate-respons
  // zodat de toggle verliesvrij aan/uit kan.
  bootstrap: PricingBootstrap | null
  bootstrapRaw: PricingBootstrap | null
  isLoadingBootstrap: boolean
  bootstrapError: string | null

  // Wederverkoper (alleen page-variant): SKU-override + minimum €150 + bundelkorting uit
  isWederverkoper: boolean
  // Heeft kantoor de toggle in deze sessie zélf bediend? Dan laat het register
  // hem met rust: een bewuste keuze (bv. een eenmalige particuliere order voor
  // een wederverkoper) mag niet door een herladen bootstrap worden overruled.
  wederverkoperHandmatigGezet: boolean
  // Buiten-werkgebied modus — automatisch bij BE/DE (customer.buitenWerkgebied)
  // of na een expliciete uitzondering (maakBuitenWerkgebiedUitzondering).
  buitenWerkgebied: boolean
  // De postcode bleek buiten alle dienstgebieden te vallen (SF success=false)
  // en er is nog géén uitzondering gemaakt — UI toont de uitzondering-prompt.
  buitenWerkgebiedDetected: boolean
  // Klant heeft geen postcode (walk-in zonder adres) en er is nog geen keuze
  // gemaakt — UI toont de zelf-brengen prompt i.p.v. een dode 400-fout.
  geenAdresDetected: boolean
  // Bootstrap draait op het wasserij-eigen gebied (Havelte-fallback) omdat de
  // klant geen postcode heeft. Zelf brengen/halen liggen dan vast: zonder adres
  // valt er niets op te halen of te bezorgen.
  wasserijFallback: boolean
  // Walk-in: kantoor koos bewust "klant brengt en haalt zelf" i.p.v. dat er
  // toevallig geen adres bekend is. Verschil met geenAdresDetected: hier mag een
  // later ingevuld adres de pricing NIET terugzetten naar het klantgebied — er
  // rijdt niets, dus geen regiotoeslag. Het adres is puur administratief.
  zelfServiceWalkIn: boolean
  // Handmatige toeslag-% override (null = DPA-default van het buiten-gebied).
  toeslagOverridePct: number | null

  // Bron van de aanvraag → Lead.LeadSource (page-variant; drawer = altijd Telefoon)
  leadSource: string

  // Cart
  items: VloerkleedCartItem[]
  nextItemId: number

  // Channel
  channel: 'ORDER' | 'QUOTE'

  // Routes (offerte-shape; één canonieke route-lookup voor /orders en /offertes)
  routes: OfferteRouteOption[]
  isLoadingRoutes: boolean
  routesError: string | null
  visibleRoutesCount: number

  // Route selection
  selectedRouteId: string | null
  preferredRouteDate: string | null

  // Capacity bump (deferred — uitgevoerd na Lead-creatie met leadId-anchor)
  capacityBumpOpen: boolean
  capacityBumpRouteId: string | null
  capacityBumpDelta: number
  capacityBumpReason: string
  capacityBumpSubmitting: boolean
  capacityBumpError: string | null
  pendingCapacityBump: PendingCapacityBump | null
  optimisticCapacityBoost: { routeId: string; delta: number } | null

  // Discount
  discountPercentage: number
  discountReason: string

  // Product
  selectedProductSku: string | null

  // Notes
  opmerkingenPlanning: string
  opmerkingenOphalen: string

  // Logistiek — kanaal-agnostische vlaggen die door-propageren naar Werk_Order__c
  zelfHalen: boolean
  zelfBrengen: boolean
  bevestigingenSturen: boolean

  // Actions
  openPanel: (customer: CustomerData) => void
  openPanelWithItems: (customer: CustomerData, items: VloerkleedCartItem[]) => void
  // Pagina-variant (/nieuwe-aanvraag): zelfde reset + bootstrap/routes als
  // openPanel, maar zónder isOpen — anders schuift de klant-detail-drawer open.
  // opts.zelfService = walk-in: prijzen op het wasserij-gebied, geen routes,
  // beide zelf-vlaggen vast aan. Adres is dan optioneel en niet prijsbepalend.
  initForPage: (customer: CustomerData, opts?: { zelfService?: boolean }) => void
  // Telefoonflow: de pagina start met alleen een postcode en vult de
  // klantgegevens pas ná de afhandelingskeuze aan. Postcode-wijziging
  // herlaadt bootstrap + routes.
  updateCustomer: (patch: Partial<CustomerData>) => void
  closePanel: () => void
  fetchBootstrap: (postcode: string) => Promise<void>
  loadRoutes: (postcode: string) => Promise<void>
  showMoreRoutes: () => void

  // Cart actions
  addItem: () => void
  removeItem: (id: string) => void
  duplicateItem: (id: string) => void
  updateItem: (id: string, updates: Partial<VloerkleedCartItem>) => void
  toggleItemExpanded: (id: string) => void

  // Product
  setSelectedProduct: (sku: string | null) => void
  setIsWederverkoper: (v: boolean) => void
  /**
   * Zet het wederverkoper-tarief aan wanneer het gekozen account in het
   * register staat (`wederverkoper_accounts`). Alleen aanzetten, en niet als
   * kantoor de toggle in deze sessie zelf heeft bediend.
   */
  syncWederverkoperUitRegister: () => Promise<void>
  setLeadSource: (source: string) => void

  // Settings
  setChannel: (channel: 'ORDER' | 'QUOTE') => void
  setSelectedRoute: (routeId: string | null, date: string | null) => void
  setDiscount: (percentage: number, reason: string) => void
  setOpmerkingenPlanning: (opm: string) => void
  setOpmerkingenOphalen: (opm: string) => void
  setZelfHalen: (v: boolean) => void
  setZelfBrengen: (v: boolean) => void
  setBevestigingenSturen: (v: boolean) => void

  // Capacity bump
  openCapacityBump: (routeId: string) => void
  closeCapacityBump: () => void
  setCapacityBumpDelta: (delta: number) => void
  setCapacityBumpReason: (reason: string) => void
  confirmCapacityBump: () => void

  // Submit — opts.leadStatus: afgezien-pad geeft de verloren-status mee zodat
  // de Lead er direct mee ontstaat (voorkomt de offerte-mail-race, zie create.ts)
  submitOrder: (opts?: { leadStatus?: string }) => Promise<void>
  setToeslagOverride: (pct: number | null) => void
  // Postcode viel buiten het werkgebied → toch doorgaan: prijzen via het vaste
  // 'Buiten werkgebied'-gebied (toeslag aanpasbaar), handmatig inplannen.
  maakBuitenWerkgebiedUitzondering: () => void
  // Klant zonder adres → order als zelf brengen + zelf halen: pricing via het
  // wasserij-eigen gebied (Havelte), geen ritten.
  maakZelfBrengenZonderAdres: () => void
}

// ─── HELPERS ────────────────────────────────────────────────

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

async function apiFetch(url: string, options?: RequestInit) {
  const token = await getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, { ...options, headers })
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || 'Request failed')
  }
  return response.json()
}

function createEmptyItem(id: number): VloerkleedCartItem {
  return {
    id: `item-${id}`,
    shape: 'rechthoekig',
    lengthCm: 0,
    widthCm: 0,
    diameterCm: 0,
    areaM2Manual: 0,
    selectedPackage: null,
    extras: [],
    notes: '',
    isExpanded: true,
    productCode: DEFAULT_OFFERTE_PRIMARY_SKU,
    aantal: 1,
  }
}

function mapItemsToLineItems(items: VloerkleedCartItem[], bootstrap: PricingBootstrap, selectedProductSku?: string | null): SubmitLineItem[] {
  const lineItems: SubmitLineItem[] = []
  const fallbackPrimary = bootstrap.availableProducts?.find(p => !p.isExtra)
  const regioFactor = 1 + (bootstrap.regioToeslagPercentage ?? 0) / 100

  items.forEach((item, idx) => {
    // Resolve per-item primary product: item-eigen productCode is leading,
    // dan panel-brede selectedProductSku, dan eerste primary uit bootstrap.
    const itemSku = item.productCode ?? selectedProductSku ?? null
    const primaryProduct = (itemSku
      ? bootstrap.availableProducts?.find(p => p.sku === itemSku)
      : null
    ) ?? fallbackPrimary
    if (!primaryProduct) return

    // ── Count-based pad (Kussen) ──────────────────────────────
    // PricingService.repriceLead herrekent server-side via Aantal__c — onze
    // client-side prijs is indicatief (zelfde gedrag als Vloerkleed).
    if (isCountBasedSku(itemSku)) {
      const aantal = item.aantal ?? 0
      if (aantal <= 0) return
      const unitPrice = primaryProduct.basePrice * regioFactor
      const totalPrice = unitPrice * aantal
      lineItems.push({
        service: 'kussen',
        sku: primaryProduct.sku,
        qty: aantal,
        unit_price: unitPrice,
        subtotal: totalPrice,
        total_price: totalPrice,
        notes: item.notes || undefined,
      })

      // Extras voor Kussen — child line-items met parent_index naar de primary.
      // qty=aantal zodat Lead_Product__c.Aantal__c klopt; PricingService reprices.
      for (const extraSku of item.extras) {
        const extra = bootstrap.availableProducts?.find(p => p.sku === extraSku)
        if (!extra) continue

        let extraTotal = 0
        if (extra.extraType === 'percentage' && extra.extraPercentage != null) {
          extraTotal = primaryProduct.basePrice * aantal * regioFactor * (extra.extraPercentage / 100)
        } else {
          extraTotal = extra.basePrice * aantal * regioFactor
        }

        lineItems.push({
          service: 'kussen',
          sku: extra.sku,
          qty: aantal,
          unit_price: extraTotal / aantal,
          subtotal: extraTotal,
          total_price: extraTotal,
          parent_sku: primaryProduct.sku,
          parent_index: idx,
        })
      }
      return
    }

    // ── Shape-based pad (Vloerkleed / Wederverkoper / Impregneren) ────
    const { areaM2Rounded } = calculateArea(item)
    if (areaM2Rounded <= 0) return

    const itemPrice = calculateVloerkleedItemPrice(item, bootstrap, selectedProductSku)

    // Primary product line item
    lineItems.push({
      service: 'vloerkleed',
      sku: primaryProduct.sku,
      qty: Math.ceil(areaM2Rounded),
      unit_price: centsToEuros(itemPrice.baseCents),
      subtotal: centsToEuros(itemPrice.baseCents),
      total_price: centsToEuros(itemPrice.baseCents),
      notes: item.notes || undefined,
      meta: {
        dims: {
          length_cm: item.lengthCm,
          width_cm: item.widthCm,
          diameter_cm: item.diameterCm,
          area_m2: areaM2Rounded,
        },
      },
    })

    // Package line item
    if (item.selectedPackage) {
      const pkg = bootstrap.packages.find(p => p.sku === item.selectedPackage)
      if (pkg) {
        lineItems.push({
          service: 'vloerkleed',
          sku: pkg.sku,
          qty: 1,
          unit_price: centsToEuros(itemPrice.packageCents - itemPrice.baseCents),
          subtotal: centsToEuros(itemPrice.packageCents - itemPrice.baseCents),
          total_price: centsToEuros(itemPrice.packageCents - itemPrice.baseCents),
          parent_sku: primaryProduct.sku,
          parent_index: idx,
        })
      }
    }

    // Extra line items
    for (const extraSku of item.extras) {
      const extra = bootstrap.availableProducts?.find(p => p.sku === extraSku)
      if (!extra) continue

      let extraPrice = 0
      if (extra.extraType === 'percentage' && extra.extraPercentage != null) {
        extraPrice = areaM2Rounded * primaryProduct.basePrice * regioFactor * (extra.extraPercentage / 100)
      } else {
        extraPrice = extra.basePrice * regioFactor
      }

      lineItems.push({
        service: 'vloerkleed',
        sku: extra.sku,
        qty: 1,
        unit_price: extraPrice,
        subtotal: extraPrice,
        total_price: extraPrice,
        parent_sku: primaryProduct.sku,
        parent_index: idx,
      })
    }
  })

  return lineItems
}

// ─── STORE ──────────────────────────────────────────────────

const ROUTE_PAGE_STEP = 3

const INITIAL_BUMP_STATE = {
  capacityBumpOpen: false,
  capacityBumpRouteId: null as string | null,
  capacityBumpDelta: 1,
  capacityBumpReason: '',
  capacityBumpSubmitting: false,
  capacityBumpError: null as string | null,
  pendingCapacityBump: null as PendingCapacityBump | null,
  optimisticCapacityBoost: null as { routeId: string; delta: number } | null,
}

const INITIAL_ROUTES_STATE = {
  routes: [] as OfferteRouteOption[],
  isLoadingRoutes: false,
  routesError: null as string | null,
  visibleRoutesCount: 3,
}

// Gedeelde verse-state voor openPanel / openPanelWithItems / initForPage —
// alles behalve isOpen (drawer vs. page beslist de caller).
function freshPanelState(customer: CustomerData, items: VloerkleedCartItem[], nextItemId: number) {
  return {
    customer,
    items,
    nextItemId,
    channel: 'ORDER' as const,
    selectedProductSku: null,
    selectedRouteId: null,
    preferredRouteDate: null,
    discountPercentage: 0,
    discountReason: '',
    opmerkingenPlanning: '',
    opmerkingenOphalen: '',
    zelfHalen: false,
    zelfBrengen: false,
    bevestigingenSturen: true,
    submitResult: null,
    bootstrap: null,
    bootstrapRaw: null,
    bootstrapError: null,
    isWederverkoper: false,
    wederverkoperHandmatigGezet: false,
    buitenWerkgebied: customer?.buitenWerkgebied === true,
    buitenWerkgebiedDetected: false,
    geenAdresDetected: false,
    wasserijFallback: false,
    zelfServiceWalkIn: false,
    toeslagOverridePct: null,
    leadSource: 'Telefoon',
    ...INITIAL_ROUTES_STATE,
    ...INITIAL_BUMP_STATE,
  }
}

/** Toeslag-override over de bootstrap heen leggen (display-pad; submit stuurt het token). */
function applyToeslagOverride<T extends { regioToeslagPercentage?: number } | null>(
  bootstrap: T,
  pct: number | null,
): T {
  if (!bootstrap || pct === null) return bootstrap
  return { ...bootstrap, regioToeslagPercentage: pct }
}

export const useOrderPanelStore = create<OrderPanelState>()((set, get) => ({
  isOpen: false,
  isSubmitting: false,
  submitResult: null,

  customer: null,

  bootstrap: null,
  bootstrapRaw: null,
  isLoadingBootstrap: false,
  bootstrapError: null,

  isWederverkoper: false,
  wederverkoperHandmatigGezet: false,

  buitenWerkgebied: false,
  buitenWerkgebiedDetected: false,
  geenAdresDetected: false,
  wasserijFallback: false,
  zelfServiceWalkIn: false,
  toeslagOverridePct: null,

  leadSource: 'Telefoon',

  items: [],
  nextItemId: 1,

  channel: 'ORDER',

  ...INITIAL_ROUTES_STATE,

  selectedProductSku: null,

  selectedRouteId: null,
  preferredRouteDate: null,

  ...INITIAL_BUMP_STATE,

  discountPercentage: 0,
  discountReason: '',

  opmerkingenPlanning: '',
  opmerkingenOphalen: '',

  zelfHalen: false,
  zelfBrengen: false,
  bevestigingenSturen: true,

  openPanel: (customer) => {
    const firstItem = createEmptyItem(1)
    set({
      isOpen: true,
      ...freshPanelState(customer, [firstItem], 2),
    })
    get().fetchBootstrap(customer.postcode)
    get().loadRoutes(customer.postcode)
  },

  initForPage: (customer, opts) => {
    const firstItem = createEmptyItem(1)
    set({
      isOpen: false,
      ...freshPanelState(customer, [firstItem], 2),
      ...(opts?.zelfService
        ? { wasserijFallback: true, zelfServiceWalkIn: true, zelfBrengen: true, zelfHalen: true }
        : {}),
    })
    if (opts?.zelfService) {
      // Lege postcode + wasserijFallback → calculate valt terug op het
      // wasserij-eigen gebied (geen regiotoeslag). Geen loadRoutes: er rijdt niets.
      get().fetchBootstrap('')
      return
    }
    get().fetchBootstrap(customer.postcode)
    get().loadRoutes(customer.postcode)
  },

  updateCustomer: (patch) => {
    const current = get().customer
    if (!current) return
    const next = { ...current, ...patch }
    const postcodeChanged =
      patch.postcode !== undefined && patch.postcode !== current.postcode
    set({ customer: next })
    if (postcodeChanged && next.postcode && get().zelfServiceWalkIn) {
      // Walk-in: het adres is administratief (factuur/klantkaart), niet
      // logistiek. Er wordt niets opgehaald of bezorgd, dus de prijs blijft op
      // het wasserij-gebied staan — een postcode uit een toeslag-gebied mag geen
      // regiotoeslag introduceren voor een rit die niemand rijdt.
      return
    }
    if (postcodeChanged && next.postcode) {
      // NL-postcode gewijzigd → werkgebied opnieuw bepalen: een eerdere
      // uitzondering (of detectie) hoort niet te blijven plakken op een
      // postcode die wél binnen het werkgebied valt. BE/DE blijft altijd
      // buiten-werkgebied (numerieke postcode zou vals matchen op NL-gebied).
      const isNL = !next.country || next.country === 'Nederland'
      if (isNL && get().buitenWerkgebied) {
        set({ buitenWerkgebied: false, toeslagOverridePct: null })
      }
      // Adres alsnog bekend → wasserij-fallback loslaten, pricing op echte
      // postcode. De zelf-vlaggen blijven staan; kantoor kan ze nu uitzetten.
      if (get().wasserijFallback) {
        set({ wasserijFallback: false, geenAdresDetected: false })
      }
      get().fetchBootstrap(next.postcode)
      get().loadRoutes(next.postcode)
    }
  },

  openPanelWithItems: (customer, items) => {
    const mappedItems = items.map((item, idx) => ({
      ...item,
      id: `reorder-${idx}`,
      isExpanded: false,
    }))
    set({
      isOpen: true,
      ...freshPanelState(customer, mappedItems, mappedItems.length + 1),
    })
    get().fetchBootstrap(customer.postcode)
    get().loadRoutes(customer.postcode)
  },

  closePanel: () => set({
    isOpen: false,
    ...INITIAL_BUMP_STATE,
  }),

  fetchBootstrap: async (postcode) => {
    // Geen postcode én geen wasserij-fallback actief: niet de API in (die geeft
    // een harde 400), maar het keuzemoment tonen — kantoor kan de order als
    // zelf brengen/halen aanmaken of eerst een adres invullen.
    if (!postcode && !get().wasserijFallback) {
      set({
        geenAdresDetected: true,
        bootstrap: null,
        bootstrapRaw: null,
        bootstrapError: null,
        isLoadingBootstrap: false,
      })
      return
    }
    set({ isLoadingBootstrap: true, bootstrapError: null })
    try {
      const data = await apiFetch('/api/orders/calculate', {
        method: 'POST',
        body: JSON.stringify({
          // Zonder postcode (wasserij-fallback) bepaalt de API de Havelte-postcode.
          ...(postcode ? { postcode } : { zelfBrengen: true }),
          ...(get().buitenWerkgebied ? { buitenWerkgebied: true } : {}),
        }),
      })

      // Postcode buiten alle dienstgebieden — geen fout, maar een keuzemoment:
      // kantoor kan een uitzondering maken (buiten-werkgebied prijzen + toeslag).
      if (data?.success === false) {
        if (!get().buitenWerkgebied) {
          set({
            buitenWerkgebiedDetected: true,
            bootstrap: null,
            bootstrapRaw: null,
            isLoadingBootstrap: false,
          })
          return
        }
        // Buiten-werkgebied aanvraag zelf faalde (vaste DPA ontbreekt) — harde fout.
        set({
          bootstrapError: 'Buiten-werkgebied prijzen niet beschikbaar — geen "Buiten werkgebied"-gebied gevonden in Salesforce.',
          isLoadingBootstrap: false,
        })
        return
      }

      // Poortwachter deel 2 — niet-Kleda-gebied blokkeren.
      // SF geeft success=true zodra ér een DPA matcht op (postcode-range + segment),
      // óók als dat een externe partner-verwijzing is (afhandelingsmethode ≠
      // 'Instant Quote'). Dit native pad is 100% Kleda's eigen wasserij (segment
      // hardcoded 'Wasserij' in api/orders/calculate.ts); daarvoor is 'Instant Quote'
      // de enige geldige methode. Alles anders (bv. 'Rest Limburg' 6100-6499 =
      // partner Mr. Kools, methode 'Quote') hoort hier niet als Kleda-offerte
      // gebouwd te worden → behandel als buiten werkgebied (kantoor kan alsnog
      // bewust een uitzondering maken via dezelfde dialog).
      if (!get().buitenWerkgebied && data?.afhandelingsmethode !== 'Instant Quote') {
        set({
          buitenWerkgebiedDetected: true,
          bootstrap: null,
          bootstrapRaw: null,
          isLoadingBootstrap: false,
        })
        return
      }

      // Default product: voorkeur voor WAS-PRIM-VLOERKLEED-REINIGEN, anders eerste primary
      const DEFAULT_VLOERKLEED_SKU = 'WAS-PRIM-VLOERKLEED-REINIGEN'
      const primaryProducts = (data?.availableProducts ?? []).filter((p: { isExtra: boolean }) => !p.isExtra)
      const hasDefault = primaryProducts.some((p: { sku: string }) => p.sku === DEFAULT_VLOERKLEED_SKU)
      const defaultSku = hasDefault
        ? DEFAULT_VLOERKLEED_SKU
        : primaryProducts[0]?.sku ?? null

      set({
        bootstrap: applyToeslagOverride(
          applyWederverkoperOverrides(data, get().isWederverkoper),
          get().toeslagOverridePct,
        ),
        bootstrapRaw: data,
        buitenWerkgebiedDetected: false,
        geenAdresDetected: false,
        isLoadingBootstrap: false,
        selectedProductSku: get().selectedProductSku ?? defaultSku,
      })

      // Wederverkoper-tarief automatisch aanzetten voor accounts die in het
      // register staan. Tot 01-09 was dit een vinkje dat kantoor moest
      // onthouden en dat standaard uit stond; vergat iemand het, dan betaalde
      // de wederverkoper het volle particuliere tarief (WB-322396, WB-323076).
      // Ná de bootstrap-set omdat setIsWederverkoper de zojuist geladen
      // producten nodig heeft om de kleden om te zetten.
      // Alleen aanzetten, nooit uitzetten: kantoor kan het bewust afvinken voor
      // een eenmalige particuliere order, en dat mag een herladen bootstrap
      // (bijvoorbeeld na een postcodewijziging) niet stilletjes terugdraaien.
      void get().syncWederverkoperUitRegister()
    } catch (error) {
      set({
        bootstrapError: error instanceof Error ? error.message : 'Failed to load pricing',
        isLoadingBootstrap: false,
      })
    }
  },

  loadRoutes: async (postcode) => {
    if (get().buitenWerkgebied) {
      // Buitenland/buiten-regio: route-lookup zou de postcode numeriek matchen
      // (BE "2321" → NL-gebied Leiden) of hard falen. Order wordt na aanmaak
      // handmatig ingepland via de route-bypass in de appointment-scheduler.
      set({ routes: [], routesError: null, isLoadingRoutes: false })
      return
    }
    if (!postcode) {
      set({ routes: [], routesError: 'Geen postcode bekend op klant' })
      return
    }
    set({ isLoadingRoutes: true, routesError: null })
    try {
      const routes = await fetchOfferteRoutes(postcode, {
        officeMode: true,
        includeFull: true,
        type: 'pickup',
      })
      set({ routes, isLoadingRoutes: false })
    } catch (err) {
      set({
        isLoadingRoutes: false,
        routesError: err instanceof Error ? err.message : 'Routes laden mislukt',
      })
    }
  },

  showMoreRoutes: () =>
    set((s) => ({
      visibleRoutesCount: Math.min(s.visibleRoutesCount + ROUTE_PAGE_STEP, s.routes.length),
    })),

  addItem: () => {
    const { nextItemId, items, isWederverkoper } = get()
    // Volgt het actieve tarief. Tot 03-10 kreeg elk kleed ná het eerste de
    // particuliere default, ook met de wederverkoper-toggle aan (WB-324506).
    const newItem = {
      ...createEmptyItem(nextItemId),
      ...(isWederverkoper ? { productCode: WEDERVERKOPER_SKU } : {}),
    }
    set({ items: [...items, newItem], nextItemId: nextItemId + 1 })
  },

  removeItem: (id) => {
    set({ items: get().items.filter(i => i.id !== id) })
  },

  duplicateItem: (id) => {
    const { items, nextItemId } = get()
    const item = items.find(i => i.id === id)
    if (!item) return
    const duplicate = { ...item, id: `item-${nextItemId}`, isExpanded: true }
    set({
      items: [...items.map(i => i.id === id ? { ...i, isExpanded: false } : i), duplicate],
      nextItemId: nextItemId + 1,
    })
  },

  updateItem: (id, updates) => {
    set({
      items: get().items.map(i => i.id === id ? { ...i, ...updates } : i),
    })
  },

  toggleItemExpanded: (id) => {
    set({
      items: get().items.map(i => i.id === id ? { ...i, isExpanded: !i.isExpanded } : i),
    })
  },

  setSelectedProduct: (sku) => {
    const { items, bootstrap } = get()
    if (!bootstrap) {
      set({ selectedProductSku: sku })
      return
    }

    // Clear package/extras selections die niet bij het nieuwe product horen
    const validPackageSkus = new Set(
      bootstrap.packages
        .filter((p) => !sku || p.relatedProductCode === sku)
        .map((p) => p.sku),
    )
    const validExtraSkus = new Set(
      (bootstrap.availableProducts ?? [])
        .filter((p) => p.isExtra && (!sku || p.relatedProductCode === sku))
        .map((p) => p.sku),
    )

    const updatedItems = items.map((item) => ({
      ...item,
      selectedPackage: item.selectedPackage && validPackageSkus.has(item.selectedPackage)
        ? item.selectedPackage
        : null,
      extras: item.extras.filter((e) => validExtraSkus.has(e)),
    }))

    set({ selectedProductSku: sku, items: updatedItems })
  },

  setIsWederverkoper: (v) => {
    const { bootstrapRaw, items } = get()
    const effectiveBootstrap = applyToeslagOverride(
      applyWederverkoperOverrides(bootstrapRaw, v),
      get().toeslagOverridePct,
    )
    const targetSku = v ? WEDERVERKOPER_SKU : DEFAULT_OFFERTE_PRIMARY_SKU

    // Alle shape-based kleden omzetten naar het doel-product; pakketten/extras
    // die niet bij het nieuwe product horen worden gestript (zelfde logica als
    // de per-item product-switch in de configurator). Count-based items
    // (Kussen) blijven ongemoeid.
    const validPackageSkus = new Set(
      (effectiveBootstrap?.packages ?? [])
        .filter((p) => p.relatedProductCode === targetSku)
        .map((p) => p.sku),
    )
    const validExtraSkus = new Set(
      (effectiveBootstrap?.availableProducts ?? [])
        .filter((p) => p.isExtra && p.relatedProductCode === targetSku)
        .map((p) => p.sku),
    )
    const updatedItems = items.map((item) => {
      if (isCountBasedSku(item.productCode)) return item
      return {
        ...item,
        productCode: targetSku,
        selectedPackage: item.selectedPackage && validPackageSkus.has(item.selectedPackage)
          ? item.selectedPackage
          : null,
        extras: item.extras.filter((e) => validExtraSkus.has(e)),
      }
    })

    set({
      isWederverkoper: v,
      wederverkoperHandmatigGezet: true,
      bootstrap: effectiveBootstrap,
      items: updatedItems,
    })
  },

  syncWederverkoperUitRegister: async () => {
    const { customer, isWederverkoper, wederverkoperHandmatigGezet } = get()
    if (!customer?.accountId) return
    if (isWederverkoper || wederverkoperHandmatigGezet) return
    try {
      const status = await apiFetch(
        `/api/customers/wederverkoper?accountId=${encodeURIComponent(customer.accountId)}`,
      )
      if (!status?.isWederverkoper) return
      // Niet via setIsWederverkoper: die zou dit als handmatige keuze stempelen
      // en daarmee een latere echte bediening door kantoor onzichtbaar maken.
      const effectiveBootstrap = applyToeslagOverride(
        applyWederverkoperOverrides(get().bootstrapRaw, true),
        get().toeslagOverridePct,
      )
      const targetSku = status.tariefSku || WEDERVERKOPER_SKU
      const validPackageSkus = new Set(
        (effectiveBootstrap?.packages ?? [])
          .filter((p) => p.relatedProductCode === targetSku)
          .map((p) => p.sku),
      )
      const validExtraSkus = new Set(
        (effectiveBootstrap?.availableProducts ?? [])
          .filter((p) => p.isExtra && p.relatedProductCode === targetSku)
          .map((p) => p.sku),
      )
      set({
        isWederverkoper: true,
        bootstrap: effectiveBootstrap,
        items: get().items.map((item) =>
          isCountBasedSku(item.productCode)
            ? item
            : {
                ...item,
                productCode: targetSku,
                selectedPackage:
                  item.selectedPackage && validPackageSkus.has(item.selectedPackage)
                    ? item.selectedPackage
                    : null,
                extras: item.extras.filter((e) => validExtraSkus.has(e)),
              },
        ),
      })
    } catch (err) {
      // Falen naar beneden: liever het particuliere tarief plus de
      // key-metrics-alert dan een particulier per ongeluk korting geven.
      console.warn('[order-panel] wederverkoper-register niet gelezen:', err)
    }
  },

  setToeslagOverride: (pct) => {
    const normalized = pct === null || Number.isNaN(pct)
      ? null
      : Math.max(0, Math.min(100, Math.round(pct)))
    set({
      toeslagOverridePct: normalized,
      bootstrap: applyToeslagOverride(
        applyWederverkoperOverrides(get().bootstrapRaw, get().isWederverkoper),
        normalized,
      ),
    })
  },

  maakBuitenWerkgebiedUitzondering: () => {
    const postcode = get().customer?.postcode
    if (!postcode) return
    set({ buitenWerkgebied: true, buitenWerkgebiedDetected: false })
    get().fetchBootstrap(postcode)
    // Guard in loadRoutes zet routes leeg — inplannen gaat handmatig.
    get().loadRoutes(postcode)
  },

  maakZelfBrengenZonderAdres: () => {
    set({
      wasserijFallback: true,
      geenAdresDetected: false,
      zelfBrengen: true,
      zelfHalen: true,
      // Zelfde strip als setZelfBrengen(true): geen rit-selectie of pending bump.
      selectedRouteId: null,
      preferredRouteDate: null,
      pendingCapacityBump: null,
      optimisticCapacityBoost: null,
      capacityBumpOpen: false,
      capacityBumpRouteId: null,
      routes: [],
      routesError: null,
    })
    // Lege postcode + wasserijFallback → API valt terug op het Havelte-gebied.
    get().fetchBootstrap('')
  },

  setLeadSource: (leadSource) => set({ leadSource }),

  setChannel: (channel) => set({ channel }),

  setSelectedRoute: (routeId, date) => {
    const state = get()
    // Bumps zijn rit-specifiek: als gebruiker een andere rit kiest na bevestigen,
    // clear de pending bump en optimistic boost zodat we niet de verkeerde rit bumpen.
    const shouldClearBump =
      state.pendingCapacityBump !== null && state.pendingCapacityBump.routeId !== routeId
    set({
      selectedRouteId: routeId,
      preferredRouteDate: date,
      ...(shouldClearBump
        ? { pendingCapacityBump: null, optimisticCapacityBoost: null }
        : {}),
    })
  },

  setDiscount: (percentage, reason) => set({ discountPercentage: percentage, discountReason: reason }),

  setOpmerkingenPlanning: (opm) => set({ opmerkingenPlanning: opm }),

  setOpmerkingenOphalen: (opm) => set({ opmerkingenOphalen: opm }),

  setZelfHalen: (v) => set({ zelfHalen: v }),

  setZelfBrengen: (v) => set(() => ({
    zelfBrengen: v,
    // Klant brengt zelf → ophaal-rit niet relevant. Strip de bestaande selectie + pending bump.
    ...(v
      ? {
          selectedRouteId: null,
          preferredRouteDate: null,
          pendingCapacityBump: null,
          optimisticCapacityBoost: null,
          capacityBumpOpen: false,
          capacityBumpRouteId: null,
        }
      : {}),
  })),

  setBevestigingenSturen: (v) => set({ bevestigingenSturen: v }),

  openCapacityBump: (routeId) => set({
    capacityBumpOpen: true,
    capacityBumpRouteId: routeId,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  closeCapacityBump: () => set({
    capacityBumpOpen: false,
    capacityBumpRouteId: null,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  setCapacityBumpDelta: (capacityBumpDelta) => set({ capacityBumpDelta }),

  setCapacityBumpReason: (capacityBumpReason) => set({ capacityBumpReason }),

  confirmCapacityBump: () => {
    const { capacityBumpRouteId, capacityBumpDelta, capacityBumpReason, routes } = get()
    if (!capacityBumpRouteId) return
    if (capacityBumpReason.trim().length < 3) {
      set({ capacityBumpError: 'Geef een reden op (min. 3 tekens).' })
      return
    }
    if (capacityBumpDelta < 1 || capacityBumpDelta > 10) {
      set({ capacityBumpError: 'Delta moet tussen 1 en 10 zijn.' })
      return
    }
    const route = routes.find((r) => r.routeId === capacityBumpRouteId)
    set({
      pendingCapacityBump: {
        routeId: capacityBumpRouteId,
        type: 'pickup',
        delta: capacityBumpDelta,
        reason: capacityBumpReason.trim(),
      },
      optimisticCapacityBoost: {
        routeId: capacityBumpRouteId,
        delta: capacityBumpDelta,
      },
      // Auto-select de gebumpte rit zodat gebruiker direct verder kan.
      selectedRouteId: capacityBumpRouteId,
      preferredRouteDate: route?.datumISO ?? null,
      capacityBumpOpen: false,
      capacityBumpRouteId: null,
      capacityBumpDelta: 1,
      capacityBumpReason: '',
      capacityBumpError: null,
    })
  },

  submitOrder: async (opts) => {
    const state = get()
    if (!state.customer || !state.bootstrap) return
    if (state.items.length === 0) return

    // Validate at least one item heeft input: count-based aantal > 0 OF shape-based area > 0
    const hasValidItem = state.items.some(item => {
      if (isCountBasedSku(item.productCode)) return (item.aantal ?? 0) > 0
      const { areaM2Rounded } = calculateArea(item)
      return areaM2Rounded > 0
    })
    if (!hasValidItem) return

    set({ isSubmitting: true, submitResult: null })

    try {
      const lineItems = mapItemsToLineItems(state.items, state.bootstrap, state.selectedProductSku)
      const totals = calculateVloerkleedTotalPrice(state.items, state.bootstrap, state.selectedProductSku)

      // Apply manual discount
      let finalSubtotalCents = totals.subtotalCents
      let finalBtwCents = totals.btwCents
      let finalTotalCents = totals.totalCents

      if (state.discountPercentage > 0) {
        const discountFactor = 1 - state.discountPercentage / 100
        finalSubtotalCents = Math.round(totals.subtotalCents * discountFactor)
        finalBtwCents = Math.round(finalSubtotalCents * 0.21)
        finalTotalCents = finalSubtotalCents + finalBtwCents
      }

      const body = {
        customer: state.customer,
        context: {
          partnerId: state.bootstrap.partnerId,
          postcodeGebiedId: state.bootstrap.postcodeGebiedId,
        },
        lineItems,
        estimate: {
          subtotal: centsToEuros(finalSubtotalCents),
          discount: centsToEuros(totals.discountCents),
          btw: centsToEuros(finalBtwCents),
          total: centsToEuros(finalTotalCents),
          currency: 'EUR',
        },
        channel: state.channel,
        leadSource: state.leadSource,
        ...(opts?.leadStatus ? { leadStatus: opts.leadStatus } : {}),
        // Bij Zelf brengen geen ophaal-rit meesturen — converter slaat dat ook over,
        // maar UI-laag is de single source of truth voor de keuze.
        ...(state.zelfBrengen
          ? {}
          : {
              ...(state.selectedRouteId ? { selectedRouteId: state.selectedRouteId } : {}),
              ...(state.preferredRouteDate ? { preferredRouteDate: state.preferredRouteDate } : {}),
            }),
        zelfHalen: state.zelfHalen,
        zelfBrengen: state.zelfBrengen,
        bevestigingenSturen: state.bevestigingenSturen,
        ...(state.buitenWerkgebied ? { buitenWerkgebied: true } : {}),
        // Alleen een expliciete override sturen — zonder token geldt de
        // DPA-default van het buiten-gebied automatisch bij de SF-reprice.
        ...(state.buitenWerkgebied && state.toeslagOverridePct !== null
          ? { toeslagPercentage: state.toeslagOverridePct }
          : {}),
        ...(state.discountPercentage > 0 ? {
          priceOverride: {
            originalPrice: centsToEuros(totals.totalCents),
            overridePrice: centsToEuros(finalTotalCents),
            discountPercentage: state.discountPercentage,
            reason: state.discountReason,
          },
        } : {}),
        ...(state.opmerkingenPlanning ? { opmerkingenPlanning: state.opmerkingenPlanning } : {}),
        ...(state.opmerkingenOphalen ? { opmerkingenOphalen: state.opmerkingenOphalen } : {}),
        // Alleen een bewuste keuze van kantoor houdt de server-omzetting naar
        // het wederverkoper-tarief tegen.
        ...(state.wederverkoperHandmatigGezet && !state.isWederverkoper
          ? { wederverkoperTariefUit: true }
          : {}),
        // Deferred capacity bump: server voert dit uit ná Lead-creatie met leadId-anchor.
        ...(state.pendingCapacityBump && !state.zelfBrengen
          ? { capacityBump: state.pendingCapacityBump }
          : {}),
      }

      const result = await apiFetch('/api/orders/create', {
        method: 'POST',
        body: JSON.stringify(body),
      })

      set({
        isSubmitting: false,
        submitResult: {
          ok: true,
          leadId: result.leadId,
          leadnummer: result.leadnummer,
          ...(result.workOrderId ? { workOrderId: result.workOrderId } : {}),
          ...(result.workOrderNumber ? { workOrderNumber: result.workOrderNumber } : {}),
          ...(result.capacityBumpWarning ? { capacityBumpWarning: result.capacityBumpWarning } : {}),
        },
      })
    } catch (error) {
      set({
        isSubmitting: false,
        submitResult: { ok: false, error: error instanceof Error ? error.message : 'Aanmaken mislukt' },
      })
    }
  },
}))
